import DecisionScoreCard from "@Components/game/session-results/decision/decision-score-card";
import { DecisionCardsRow, NavigationButton, NoDataContainer } from "@Components/game/session-results/decision/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { AnalyticsEvents } from "@Enums/index";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { getDecisionTitle } from "@hooks/common/session/decision-score-rows";
import {
  buildReplayPreviewPayload,
  getDecisionOptionId,
  getDecisionOptionTime,
  getDisplayedDecisionOptions,
} from "@hooks/common/session/replay-decision-utils";
import { SessionFeedbackModel } from "@hooks/game/activity/session/use-session-feedback-model";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";

interface IDecisionScoresSection {
  feedbackModel: SessionFeedbackModel;
}

const DecisionScoresSection = ({ feedbackModel }: IDecisionScoresSection) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { track } = useAnalytics();
  const { replay, playback, actions } = feedbackModel;

  if (replay.areDecisionsLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <NavigationButton disabled aria-hidden="true" />

        <DecisionCardsRow>
          {Array.from({ length: 3 }, (_, index) => (
            <DecisionScoreCard key={`loading-${index}`} loading />
          ))}
        </DecisionCardsRow>

        <NavigationButton disabled aria-hidden="true" />
      </Stack>
    );
  }

  if (replay.decisions.length <= 0) {
    return (
      <NoDataContainer direction="column" spacing={2}>
        <Typography variant="textLg600" color="dsBase.v200">
          {i18n("session-results.feedback.no-data")}
        </Typography>
      </NoDataContainer>
    );
  }

  const currentDecision = replay.decisions[playback.highlightPointIndex];
  if (!currentDecision) {
    return null;
  }

  const { didUserMakeDecision, displayedOptions, maxScore } = getDisplayedDecisionOptions(currentDecision);

  const canGoPrevious = replay.decisions && playback.highlightPointIndex > 0;
  const canGoNext = replay.decisions && playback.highlightPointIndex < (replay.decisions?.length || 0) - 1;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {canGoPrevious ? (
        <HapticsButtonWrapper>
          <NavigationButton onClick={actions.handlePreviousDecision} aria-label={i18n("session-results.previous-decision")}>
            <Icon icon={faChevronLeft} color={theme.palette.dsNeutral.v300} size="lg" />
          </NavigationButton>
        </HapticsButtonWrapper>
      ) : (
        <NavigationButton disabled aria-label={i18n("session-results.previous-decision")} aria-disabled="true">
          <Icon icon={faChevronLeft} color={theme.palette.dsNeutral.v300} size="lg" />
        </NavigationButton>
      )}

      <DecisionCardsRow>
        {displayedOptions.map((option, index) => {
          const score = option?.displayScore ?? 0;
          const previewPayload = option && replay.replayData ? buildReplayPreviewPayload(option, replay.replayData, currentDecision.startTime) : null;
          const decisionTime = getDecisionOptionTime(option, currentDecision.startTime);
          const title = getDecisionTitle(index, didUserMakeDecision, i18n, "long");

          return (
            <HapticsButtonWrapper key={option ? getDecisionOptionId(option) : `empty-user:${currentDecision.startTime}`}>
              <DecisionScoreCard
                title={title}
                score={score}
                showScore={didUserMakeDecision || index > 0}
                isBestOption={score > 0 && score === maxScore}
                isSelected={playback.selectedCard === index}
                onClick={() => {
                  if (index > 0 && playback.selectedCard !== index) {
                    const eventData = {
                      ...(feedbackModel.analytics.scenario ?? {}),
                      decision_title: title,
                    };

                    track(AnalyticsEvents.alternative_decision_selected, eventData);
                  }

                  actions.setSelectedCard(index);

                  if (!previewPayload) {
                    actions.setIsPlaying(false);
                    return;
                  }

                  actions.seekTo(decisionTime);
                }}
              />
            </HapticsButtonWrapper>
          );
        })}
      </DecisionCardsRow>

      {canGoNext ? (
        <HapticsButtonWrapper>
          <NavigationButton onClick={actions.handleNextDecision} aria-label={i18n("session-results.next-decision")}>
            <Icon icon={faChevronRight} color={theme.palette.dsNeutral.v300} size="lg" />
          </NavigationButton>
        </HapticsButtonWrapper>
      ) : (
        <NavigationButton disabled aria-label={i18n("session-results.next-decision")} aria-disabled="true">
          <Icon icon={faChevronRight} color={theme.palette.dsNeutral.v300} size="lg" />
        </NavigationButton>
      )}
    </Stack>
  );
};

export default DecisionScoresSection;
